import { Routes, Route, Navigate } from 'react-router-dom';
import React, { useEffect, useContext, useState, Suspense } from 'react';
import Header from '../header';
import Footer from '../footer';
import NavigationSidebar from '../navigationsidebar';
import { NavigationSidebarContext } from '../../contexts/navigationSidebar';
import { ProtectedRoute } from './protectedRoute';
import { AuthContext } from '../../contexts/auth';
import { AuthProvider } from '../../contexts/auth';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import i18next from 'i18next';
import { LanguageContext } from '../../contexts/language';

const SignIn = React.lazy(() => import('../../pages/signin'));
const QuickResponse = React.lazy(() => import('../../pages/qr'));
const ExpressQuickResponse = React.lazy(() => import('../../pages/eQR'));
const TerminalQuickResponse = React.lazy(() => import('../../pages/tqr'));
const About = React.lazy(() => import('../../pages/about'));
const PageNotFound = React.lazy(() => import('../../pages/403'));
const StationDetail = React.lazy(() => import('../../pages/manage-stations/stationDetails'));
const VehicleDetails = React.lazy(() => import('../../pages/vehicleReport/VehicleDetails'));
const VehicleStationDetails = React.lazy(() => import('../../pages/vehicleReport/VehicleStationDetails'));
const AssignRoutes = React.lazy(() => import('../../pages/manage-stations/AssignRoutes'));
const AgentAssignRoutes = React.lazy(() => import('../../pages/manage-stations/AgentAssignRoutes'));
const AgentSummary = React.lazy(() => import('../../pages/dashboard/stationDashboard/AgentSummary'));
const VehicleReport = React.lazy(() => import('../../pages/vehicleReport'));

function Layout() {
    const { en_web } = useContext(LanguageContext);
    const { am_web } = useContext(LanguageContext);
    const { or_web } = useContext(LanguageContext);

    useEffect(() => {
        i18next.init({
            interpolation: { escapeValue: false },
            lng: "en",
            resources: {
                en: {
                    global: en_web
                },
                am: {
                    global: am_web
                },
                ao: {
                    global: or_web
                }
            }
        });
    }, [en_web, am_web, or_web]);

    return (
        <Suspense fallback={<div className='flex justify-center items-center h-screen'>Loading...</div>}>
            <Routes>
                <Route path="/signin" element={
                    <AuthProvider>
                        <SignIn />
                    </AuthProvider>
                } />
                <Route path="/qr/:code" element={<QuickResponse />} />
                <Route path="/eqr/:code" element={<ExpressQuickResponse />} />
                <Route path="/tqr/:code" element={<TerminalQuickResponse />} />
                <Route path="/403" element={
                    <AuthProvider>
                        <PageNotFound />
                    </AuthProvider>
                } />
                <Route path="/about" element={<About />} />
                <Route path="*" element={
                    <AuthProvider>
                        <ResponsiveLayout />
                    </AuthProvider>
                } />
            </Routes>
        </Suspense>
    );
}

function ResponsiveLayout() {
    const { isNavigationSidebarOpen } = useContext(NavigationSidebarContext);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const isMobile = screenWidth < 768;
    const display = (isNavigationSidebarOpen || !isMobile) ? 'block' : 'hidden';
    const { user } = useContext(AuthContext);

    if (!user) {
        return (
            <div className="flex items-center justify-center h-screen">
                <ArrowPathIcon className="animate-spin h-10 w-10 text-gray-500" />
                <span className="ml-2 text-gray-500">Loading user data, please wait...</span>
            </div>
        );
    }

    return (
        <div className='flex h-screen'>
            <NavigationSidebar />
            <div className={`flex flex-col justify-between flex-grow overflow-y-auto ${display}`}>
                <Header />
                <main className='flex-grow'>
                    <AuthenticatedRoutes />
                </main>
                <Footer />
            </div>
        </div>
    );
}

function AuthenticatedRoutes() {
    return (
        <Suspense fallback={<div className='flex justify-center items-center h-screen'>Loading authenticated routes...</div>}>
            <Routes>
                <Route path="/" element={<ProtectedRoute />} />
                <Route path="/ministry" element={<ProtectedRoute />} />
                <Route path="/profile" element={<ProtectedRoute />} />
                <Route path="/payments" element={<ProtectedRoute />} />
                <Route path="/station" element={<ProtectedRoute />} />
                <Route path="/VehicleReport" element={<ProtectedRoute />} />
                <Route path="/stationDashboard" element={<ProtectedRoute />} />
                <Route path="/agents" element={<ProtectedRoute />} />
                <Route path="/manage-associations/" element={<ProtectedRoute />} />
                <Route path="/manage-users/" element={<ProtectedRoute />} />
                <Route path="/manage-roles/" element={<ProtectedRoute />} />
                <Route path="/locations/" element={<ProtectedRoute />} />
                <Route path="/manage-stations" element={<ProtectedRoute />} />
                <Route path="/reservations" element={<ProtectedRoute />} />
                <Route path="/manage-agents/" element={<ProtectedRoute />} />
                <Route path="/manage-routes/" element={<ProtectedRoute />} />
                <Route path="/manage-app-sync/" element={<ProtectedRoute />} />
                <Route path="/manage-app-file/" element={<ProtectedRoute />} />
                <Route path="/manage-language" element={<ProtectedRoute />} />
                <Route path="/vehicle" element={<ProtectedRoute />} />
                <Route path="/fleet" element={<ProtectedRoute />} />
                <Route path="/stationDetail/:id" element={<StationDetail />} />
                <Route path="/vehicleDetails" element={<VehicleDetails />} />
                <Route path="/VehicleStationDetails" element={<VehicleStationDetails />} />
                <Route path="/assignRoutes" element={<AssignRoutes />} />
                <Route path="/agentassignRoutes" element={<AgentAssignRoutes />} />
                <Route path="/agentSummary" element={<AgentSummary />} />
                <Route path="/vehiclesSummary" element={<VehicleReport />} />
                <Route path="/terminal-exit/tickets" element={<ProtectedRoute />} />
                <Route path="/terminal-exit/receipts" element={<ProtectedRoute />} />
                <Route path="/finance-page" element={<ProtectedRoute />} />
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </Suspense>
    );
}

export default Layout;
